import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { savedMealsService } from '../../../services/savedMealsService';

const MEAL_TYPE_LABELS = {
  breakfast: { label: 'Desayuno', icon: 'Coffee', color: 'text-amber-500', bg: 'bg-amber-500/10' },
  lunch: { label: 'Almuerzo', icon: 'Utensils', color: 'text-green-500', bg: 'bg-green-500/10' },
  dinner: { label: 'Cena', icon: 'ChefHat', color: 'text-purple-500', bg: 'bg-purple-500/10' },
  snack: { label: 'Snack', icon: 'Cookie', color: 'text-rose-500', bg: 'bg-rose-500/10' },
};

const FILTER_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'breakfast', label: 'Desayuno' },
  { value: 'lunch', label: 'Almuerzo' },
  { value: 'dinner', label: 'Cena' },
  { value: 'snack', label: 'Snack' },
];

const CollectionCard = ({ collection, onQuickAdd, onDelete, addingId }) => {
  const [showItems, setShowItems] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const typeInfo = MEAL_TYPE_LABELS?.[collection?.meal_type] || MEAL_TYPE_LABELS?.snack;
  const items = collection?.saved_meal_items || [];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="bg-muted/20 border border-border rounded-xl overflow-hidden"
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`flex items-center justify-center w-9 h-9 ${typeInfo?.bg} rounded-lg shrink-0`}>
              <Icon name={typeInfo?.icon} size={16} className={typeInfo?.color} />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-foreground text-sm truncate">{collection?.name}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-xs font-medium ${typeInfo?.color}`}>{typeInfo?.label}</span>
                {collection?.use_count > 0 && (
                  <span className="text-xs text-muted-foreground">• usado {collection?.use_count}x</span>
                )}
              </div>
            </div>
          </div>

          {/* Quick Add Button */}
          <button
            onClick={() => onQuickAdd(collection)}
            disabled={addingId === collection?.id}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-semibold hover:bg-primary/90 active:scale-95 transition-all disabled:opacity-60 shrink-0"
          >
            {addingId === collection?.id ? (
              <Icon name="Loader2" size={13} className="animate-spin" />
            ) : (
              <Icon name="Zap" size={13} />
            )}
            {addingId === collection?.id ? 'Agregando...' : 'Quick Add'}
          </button>
        </div>

        {/* Nutrition summary */}
        <div className="grid grid-cols-4 gap-1.5 mt-3">
          {[
            { label: 'Cal', value: collection?.total_calories, color: 'text-primary' },
            { label: 'P', value: `${Math.round(collection?.total_protein || 0)}g`, color: 'text-blue-500' },
            { label: 'C', value: `${Math.round(collection?.total_carbs || 0)}g`, color: 'text-amber-500' },
            { label: 'G', value: `${Math.round(collection?.total_fat || 0)}g`, color: 'text-rose-500' },
          ]?.map(stat => (
            <div key={stat?.label} className="text-center p-1.5 bg-background/60 rounded-lg">
              <p className={`text-xs font-bold ${stat?.color}`}>{stat?.value}</p>
              <p className="text-xs text-muted-foreground">{stat?.label}</p>
            </div>
          ))}
        </div>

        {/* Toggle items & delete */}
        <div className="flex items-center justify-between mt-3">
          <button
            onClick={() => setShowItems(v => !v)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name={showItems ? 'ChevronUp' : 'ChevronDown'} size={13} />
            {items?.length} alimento{items?.length !== 1 ? 's' : ''}
          </button>

          {confirmDelete ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">¿Eliminar?</span>
              <button
                onClick={() => onDelete(collection?.id)}
                className="text-xs text-destructive font-medium hover:underline"
              >
                Sí
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="text-xs text-muted-foreground hover:underline"
              >
                No
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDelete(true)}
              className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
            >
              <Icon name="Trash2" size={13} />
            </button>
          )}
        </div>
      </div>
      {/* Items list */}
      <AnimatePresence>
        {showItems && items?.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-t border-border"
          >
            <div className="p-3 space-y-1.5 bg-background/40">
              {items?.map((item, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon name="Dot" size={14} className="text-primary shrink-0" />
                    <span className="text-foreground truncate">{item?.food_name}</span>
                  </div>
                  <span className="text-muted-foreground ml-2 shrink-0">{item?.calories} cal</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const SavedMealsPanel = ({ userId, onQuickAdd, isOpen, onClose, onLoadError }) => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [addingId, setAddingId] = useState(null);
  const [successId, setSuccessId] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const loadCollections = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setLoadError(false);
    try {
      const data = await savedMealsService?.getCollections(userId);
      setCollections(data);
    } catch (err) {
      console.error('Error loading saved meals:', err);
      setLoadError(true);
      onLoadError?.();
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen) loadCollections();
  }, [isOpen, loadCollections]);

  const handleQuickAdd = async (collection) => {
    setAddingId(collection?.id);
    try {
      await onQuickAdd(collection);
      setSuccessId(collection?.id);
      setTimeout(() => setSuccessId(null), 2000);
      // Increment use count (non-blocking)
      savedMealsService?.incrementUseCount(collection?.id)?.catch(() => {});
      // Update local use_count
      setCollections(prev =>
        prev?.map(c => c?.id === collection?.id ? { ...c, use_count: (c?.use_count || 0) + 1 } : c)
      );
    } catch (err) {
      console.error('Error quick-adding meal:', err);
    } finally {
      setAddingId(null);
    }
  };

  const handleDelete = async (collectionId) => {
    try {
      await savedMealsService?.deleteCollection(collectionId);
      setCollections(prev => prev?.filter(c => c?.id !== collectionId));
    } catch (err) {
      console.error('Error deleting collection:', err);
    }
  };

  const filtered = filter === 'all'
    ? collections
    : collections?.filter(c => c?.meal_type === filter);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.25 }}
      className="bg-card rounded-xl elevation-2 overflow-hidden flex flex-col"
      style={{ maxHeight: '600px' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg">
            <Icon name="Bookmark" size={16} className="text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-sm">Comidas Guardadas</h3>
            <p className="text-xs text-muted-foreground">{collections?.length} colección{collections?.length !== 1 ? 'es' : ''}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-muted/50 transition-colors"
        >
          <Icon name="X" size={16} className="text-muted-foreground" />
        </button>
      </div>
      {/* Filter tabs */}
      <div className="flex gap-1 p-3 border-b border-border overflow-x-auto shrink-0">
        {FILTER_OPTIONS?.map(opt => (
          <button
            key={opt?.value}
            onClick={() => setFilter(opt?.value)}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
              filter === opt?.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted/30 text-muted-foreground hover:bg-muted/60'
            }`}
          >
            {opt?.label}
          </button>
        ))}
      </div>
      {/* Collections list */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <Icon name="Loader2" size={24} className="text-primary animate-spin" />
            <p className="text-sm text-muted-foreground">Cargando colecciones...</p>
          </div>
        ) : loadError ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
            <div className="w-12 h-12 bg-destructive/10 rounded-xl flex items-center justify-center">
              <Icon name="AlertCircle" size={22} className="text-destructive" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Error al cargar comidas</p>
              <p className="text-xs text-muted-foreground mt-1">No se pudieron cargar tus comidas guardadas</p>
            </div>
            <button
              onClick={loadCollections}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors"
            >
              <Icon name="RefreshCw" size={13} />
              Reintentar
            </button>
          </div>
        ) : filtered?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
            <div className="w-12 h-12 bg-muted/30 rounded-xl flex items-center justify-center">
              <Icon name="BookmarkX" size={22} className="text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">
                {filter === 'all' ? 'No tienes comidas guardadas' : `No hay colecciones de ${MEAL_TYPE_LABELS?.[filter]?.label || filter}`}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Guarda una comida usando el botón "Guardar Comida" en cada sección
              </p>
            </div>
          </div>
        ) : (
          <AnimatePresence mode="popLayout">
            {filtered?.map(collection => (
              <CollectionCard
                key={collection?.id}
                collection={collection}
                onQuickAdd={handleQuickAdd}
                onDelete={handleDelete}
                addingId={addingId}
                successId={successId}
              />
            ))}
          </AnimatePresence>
        )}
      </div>
    </motion.div>
  );
};

export default SavedMealsPanel;
